import { atom } from "jotai";
import {
    allActiveLocationsAtom,
    allActiveServicesAtom,
    clientPackagesAtom,
    companyAtom,
    companyCurrencyAtom,
    companyMasterTz,
    getLocationByIdAtom,
    getProviderByProviderIdAtom,
    getProviderSchedulesByProviderIdAtom,
    getProviderServicesByProviderIdAtom
} from "./atoms";
import {
    setRecurringDetailAtom,
    rruleAtom,
    rfcRRuleSettingsAtom,
    startDateAtom,
    frequencyAtom,
    intervalAtom,
    weekdaysAtom,
    endDateAtom,
    monthlyModeAtom,
    yearlyModeAtom,
    setWeekdaysAtom,
    mbRecurringBookingRRuleAtom
} from "./recurringSettings";
import { format, utcToZonedTime } from "date-fns-tz";
import { getCountryCode, getSlotType, jsonParse } from "../utils";
import { uniq, flatten, filter, find, map, pick } from "lodash";
import {
    CLIENT_LOCATION_ID,
    DEFAULT_BOOKING_DETAILS,
    DEFAULT_CURRENCY,
    DEFAULT_STRIPE_CHARGE_CARD_STATE,
    PAY_TYPE_CC,
    PAY_TYPE_NEW_CC,
    PAY_TYPE_PACKAGE
} from "../utils/Constants";
import {
    formatRawMoneyAmount,
    prepareBookingsDateTimeList
} from "../modules/AdhocBookingService";
import { getServiceTaxRate } from "../modules/TaxService";
import { getDescription } from "../modules/AdhocBookingService";
import { currencyFormat } from "../modules/AdhocBookingService";
import { focusAtom } from "jotai-optics";
import {
    formatDateInfoAttributes,
    getTimezoneIdByGeoLoc
} from "../modules/TimeService";
import { getDay, parseISO } from "date-fns";
import { timezoneConversion } from "../utils/CalendarFunctions/timezoneFunctions";
import { endModeAtom, countAtom, rfcRRuleAtom } from "./recurringSettings";
import { RRule } from "rrule";
export const showBookingDetailsAtom = atom(false);
export const bookingDetailsAtom = atom(DEFAULT_BOOKING_DETAILS);
export const days = [
    { id: 0, title: "S" },
    { id: 1, title: "M" },
    { id: 2, title: "T" },
    { id: 3, title: "W" },
    { id: 4, title: "T" },
    { id: 5, title: "F" },
    { id: 6, title: "S" }
];
export const dayMap = {
    MO: "Monday",
    TU: "Tuesday",
    WE: "Wednesday",
    TH: "Thursday",
    FR: "Friday",
    SA: "Saturday",
    SU: "Sunday"
};
export const RECURRING_PATTERN_TOTAL = "total";
export const RECURRING_PATTERN_INDEF = "indef";
export const RECURRING_PATTERN_UNTIL = "until";
export const getSelectedServiceAtom = atom((get) => {
    const { selectedServiceId } = get(bookingDetailsAtom);
    return find(get(allActiveServicesAtom), ["id", selectedServiceId]);
});
export const getSelectedLocationAtom = atom((get) => {
    const { selectedLocationId, clientAddress } = get(bookingDetailsAtom);
    return selectedLocationId === CLIENT_LOCATION_ID
        ? clientAddress
        : find(get(allActiveLocationsAtom), ["id", selectedLocationId]);
});
export const isRemoteBookingAtom = atom(
    (get) => get(bookingDetailsAtom)?.selectedLocationId === CLIENT_LOCATION_ID
);
export const canCheckoutAtom = atom((get) => {
    const {
        selectedLocationId,
        selectedServiceId,
        client,
        sessions,
        isBookSeparately,
        provider
    } = get(bookingDetailsAtom);
    const selectedLocation = get(getSelectedLocationAtom);
    return (
        selectedLocation &&
        selectedServiceId &&
        client &&
        (sessions?.length || isBookSeparately) &&
        provider
    );
});

export const chargeAtom = atom((get) => {
    const {
        price,
        selectedPackage,
        isPkgCredit,
        overrideServicePrice,
        overrideServiceFee
    } = get(bookingDetailsAtom);
    const selectedLocation = get(getSelectedLocationAtom);
    const company = get(companyAtom);
    const { state, countrycode3166alpha2 } = selectedLocation;
    const taxJurisdiction = { countryCode: "", stateCode: "" };
    const { TaxOverride, Settings: exemptServiceFeeJson } = get(
        getSelectedServiceAtom
    );
    taxJurisdiction.countryCode = countrycode3166alpha2;
    taxJurisdiction.stateCode = state;
    let taxOverrideValue = null;
    try {
        taxOverrideValue = JSON.parse(TaxOverride);
    } catch {
        console.log("Error parsing tax override value");
    }
    let taxableAmount = 0;
    const charge = {
        subtotal: 0,
        taxableAmount: 0,
        serviceChargeAmount: 0,
        total: 0,
        taxRate: null
    };

    //Assign the subtotal value
    if (selectedPackage) {
        const {
            price: packagePrice,
            quantity,
            discount,
            packagetype
        } = selectedPackage?.status && selectedPackage?.status === "paid"
            ? selectedPackage?.package
            : selectedPackage;
        if (isPkgCredit) {
            charge.subtotal = 0;
        } else {
            charge.subtotal = overrideServicePrice
                ? Number.parseFloat(overrideServicePrice)
                : packagetype === "DOLLAR"
                  ? packagePrice
                  : quantity * price * (1 - discount / 100);
        }
    } else {
        charge.subtotal = overrideServicePrice
            ? Number.parseFloat(overrideServicePrice)
            : price;
    }

    //Assign the tax rate
    let taxRate =
        !isNaN(parseFloat(taxOverrideValue)) && isFinite(taxOverrideValue)
            ? parseFloat(taxOverrideValue)
            : getServiceTaxRate(countrycode3166alpha2, state);
    charge.taxRate = taxRate;
    if (
        ["US", "AU", "NZ", "GB"].includes(
            countrycode3166alpha2.toUpperCase()
        ) &&
        charge.taxRate === 0 &&
        company.taxrate !== undefined &&
        company.taxrate !== null
    ) {
        charge.taxRate = company.taxrate;
    }
    let exemptServiceFee = false;
    try {
        const settings = JSON.parse(exemptServiceFeeJson);
        if (settings && settings?.exemptServiceFees) exemptServiceFee = true;
    } catch {
        console.log("Unable to parse service settings");
    }
    //Assign the service fee amount
    if (!exemptServiceFee && company.addServiceFee && !isPkgCredit) {
        if (overrideServiceFee) {
            charge.serviceChargeAmount = Number.parseFloat(overrideServiceFee);
        } else {
            if (company.serviceFeeType === "PERCENTAGE") {
                charge.serviceChargeAmount = Number.parseFloat(
                    (price * company.serviceFeeAmount) / 100
                );
            } else {
                charge.serviceChargeAmount = Number.parseFloat(
                    company.serviceFeeAmount
                );
            }
        }
    }

    //Calculate the taxable amount
    taxableAmount +=
        charge.subtotal +
        (countrycode3166alpha2 === "ca" && charge.serviceChargeAmount > 0
            ? charge.serviceChargeAmount
            : 0);

    let taxAmount = (taxableAmount * taxRate) / 100;
    charge.taxableAmount = taxableAmount;
    charge.taxAmount = taxAmount;
    charge.total =
        charge.subtotal + charge.serviceChargeAmount + charge.taxAmount;
    return charge;
});
export const stripeChargeCardStateAtom = atom(DEFAULT_STRIPE_CHARGE_CARD_STATE);
export const isStripeChargeCardValidAtom = atom((get) => {
    const { cardNumber, cardExpiry, cardCvc } = get(stripeChargeCardStateAtom);
    return (
        cardNumber.complete &&
        !cardNumber.error &&
        cardExpiry.complete &&
        !cardExpiry.error &&
        cardCvc.complete &&
        !cardCvc.error
    );
});
export const bookingTypeAtom = atom((get) => {
    const { isRepeating, selectedPackage } = get(bookingDetailsAtom);
    return selectedPackage ? "package" : isRepeating ? "forever" : "single";
});
export const orderSummaryAtom = atom((get) => {
    const {
        price,
        selectedPackage,
        overrideServicePrice,
        isRepeating,
        repeatingAppointmentList,
        sessions,
        payType
    } = get(bookingDetailsAtom);
    const currency = get(companyCurrencyAtom) || DEFAULT_CURRENCY;
    const selectedService = get(getSelectedServiceAtom);
    const sdt =
        payType === PAY_TYPE_PACKAGE
            ? "package"
            : isRepeating
              ? "forever"
              : "single";

    const orderSummary = { rows: [] };
    let creditsRemaining = 0;
    let charge = {};
    const pkg =
        selectedPackage?.status === "paid"
            ? selectedPackage.package
            : selectedPackage;

    //  existing pkg
    if (payType === PAY_TYPE_PACKAGE || selectedPackage) {
        const numofappts = sessions?.length;
        const clientPackage = find(get(clientPackagesAtom), [
            "id",
            selectedPackage.id
        ]);
        if (clientPackage) {
            orderSummary.rows.push({
                service: `Previously purchased package of ${
                    clientPackage.initialQuantity
                } sessions - ${
                    clientPackage.initialQuantity - clientPackage.usedQuantity
                } of ${clientPackage.initialQuantity} remaining`,
                qty: `${numofappts} of ${
                    clientPackage.initialQuantity - clientPackage.usedQuantity
                } credits used`,
                price: currencyFormat(0, currency),
                rawPrice: Number.parseFloat(0).toFixed(2)
            });
            charge = get(chargeAtom);
            creditsRemaining =
                clientPackage.initialQuantity -
                clientPackage.usedQuantity -
                numofappts;
        } else if (selectedPackage) {
            // calculate subtotal based on package type
            // discount for the taxableAmount is calculated in the totalTaxableAmount function
            // dollar packages use the original price value (unchanged by regional pricing) in the boughtpackage object
            // percentage packages use the regionalServicePrice value
            const subtotal = overrideServicePrice
                ? overrideServicePrice
                : pkg.packagetype === "DOLLAR"
                  ? pkg.price
                  : pkg.quantity * price * (1 - pkg.discount / 100);

            const description = getDescription(pkg, price, currency);

            orderSummary.rows.push({
                service: description,
                qty: `${numofappts} of ${pkg.quantity} credits used`,
                price: currencyFormat(subtotal, currency),
                rawPrice: Number.parseFloat(subtotal).toFixed(2)
            });
            charge = get(chargeAtom);
        }
    } else {
        const service =
            payType === PAY_TYPE_PACKAGE && !isRepeating
                ? selectedService.name
                : `${selectedService.name}`;
        const qty = 1; //single or forever
        const rowPrice = overrideServicePrice
            ? Number.parseFloat(overrideServicePrice)
            : price * qty;

        orderSummary.rows.push({
            service,
            qty,
            price: currencyFormat(rowPrice, currency),
            rawPrice: Number.parseFloat(rowPrice).toFixed(2)
        });

        charge = get(chargeAtom);
    }
    orderSummary.subtotal = currencyFormat(charge.subtotal, currency);
    orderSummary.serviceFee = currencyFormat(
        charge.serviceChargeAmount,
        currency
    );
    orderSummary.rawServiceFee = formatRawMoneyAmount(
        charge.serviceChargeAmount
    );
    orderSummary.taxRate = charge.taxRate;
    orderSummary.taxAmount = currencyFormat(charge.taxAmount, currency);
    orderSummary.total = currencyFormat(charge.total, currency);
    orderSummary.taxableAmount = currencyFormat(charge.taxableAmount, currency);

    return {
        orderSummary,
        charge,
        creditsRemaining,
        taxExemption: charge.taxableAmount !== charge.subtotal
    };
});

export const getDisplayLocationOptionsAtom = atom((get) =>
    get(bookingDetailsAtom)
        ?.locations?.filter((id) => id?.startsWith("CL"))
        .map((id) => ({
            id,
            name: get(allActiveLocationsAtom).find(
                ({ id: companyLocationId }) =>
                    companyLocationId === id.substring(3)
            )?.locationname
        }))
);
export const timeDisplayInfoAtom = atom((get) => {
    const selectedService = get(getSelectedServiceAtom);
    const { sessions, provider, selectedLocationId } = get(bookingDetailsAtom);
    const location = get(getLocationByIdAtom)(selectedLocationId);
    if (!sessions?.length || !provider) return null;
    const providerTzDateTimeIso = sessions[0].providerTzDateTimeIso;

    const locTzDate = timezoneConversion(
        providerTzDateTimeIso,
        location.timezone
    );
    return formatDateInfoAttributes(
        locTzDate,
        selectedService?.minutes || null,
        location?.id === CLIENT_LOCATION_ID
            ? provider?.timezone
            : location?.timezone
    );
});

// const count2Atom = atomWithDefault((get) => get(count1Atom) * 2)
export const setDetailAtom = atom(null, (get, set, newDetail) => {
    let newState = { ...newDetail };
    const companyTz = get(companyMasterTz);
    const detailName = Object.keys(newDetail)[0];
    const detailValue = newDetail[detailName];
    const currentState = get(bookingDetailsAtom);
    const {
        schedules,
        sessions,
        provider,
        isRepeating,
        selectedPackage,
        selectedPackageRemainingQuantity,
        selectedLocationId,
        client
    } = currentState;

    switch (detailName) {
        case "providerId":
            /*  const providerSchedules = get(getProviderSchedulesByProviderIdAtom)(
                detailValue
            ); */
            newState.providerId = detailValue;
            newState.provider = get(getProviderByProviderIdAtom)(detailValue);
            newState.providerServices = get(
                getProviderServicesByProviderIdAtom
            )(detailValue);
            newState.providerLocationIds = uniq(
                flatten(
                    filter(schedules, ({ services }) =>
                        jsonParse(services, []).includes(
                            currentState.selectedServiceId
                        )
                    ).map(({ locations }) => jsonParse(locations, []))
                )
            );
            newState.sessions = map(sessions, (session, index) => {
                if (sessions[index]?.dateTime) {
                    const selectedService = get(getSelectedServiceAtom);
                    const selectedLocation = get(getSelectedLocationAtom);
                    //Retrieve the selected dateTime formatted to providers timezone
                    const {
                        dtstamp_str: providerTzDateTimeIso,
                        tz_abbr_disp: providerTzAbbr
                    } = formatDateInfoAttributes(
                        sessions[index].dateTime,
                        selectedService?.minutes || null,
                        newState.provider
                            ? newState.provider?.timezone
                            : companyTz
                    );
                    //Retrive the selected dateTime formatted to locations timezone
                    const locTzDate = timezoneConversion(
                        providerTzDateTimeIso,
                        selectedLocation?.id === CLIENT_LOCATION_ID
                            ? newState.provider
                                ? newState.provider?.timezone
                                : companyTz
                            : selectedLocation
                              ? selectedLocation?.timezone
                              : companyTz
                    );

                    const locationTzDateInfo = formatDateInfoAttributes(
                        locTzDate,
                        selectedService?.minutes || null,
                        selectedLocation?.id === CLIENT_LOCATION_ID
                            ? newState.provider
                                ? newState.provider?.timezone
                                : companyTz
                            : selectedLocation
                              ? selectedLocation?.timezone
                              : companyTz
                    );

                    if (sessions[index]) {
                        sessions[index].providerTzDateTimeIso =
                            providerTzDateTimeIso;
                        sessions[index].providerTzAbbr = providerTzAbbr;
                        sessions[index].locationTzDateTimeIso =
                            locationTzDateInfo.dtstamp_str;
                        sessions[index].locationTzDate =
                            locationTzDateInfo.dt_disp;
                        sessions[index].locationTzTime =
                            locationTzDateInfo.label;
                        sessions[index].locationTzDateInfo = locationTzDateInfo;
                        newState.sessions = [...sessions];
                    }
                }

                return {
                    ...session,
                    providerId: detailValue
                };
            });
            break;
        case "selectedServiceId":
            newState.providerLocationIds = uniq(
                flatten(
                    filter(schedules, ({ services }) =>
                        jsonParse(services, []).includes(detailValue)
                    ).map(({ locations }) => jsonParse(locations, []))
                )
            );
            break;
        case "selectedLocationId":
            newState.providerServices = uniq(
                flatten(
                    filter(schedules, ({ locations }) =>
                        jsonParse(locations, []).includes(detailValue)
                    ).map(({ services }) => jsonParse(services, []))
                )
            );

            const newLocation = get(getLocationByIdAtom)(detailValue);
            //updating locationTzDateTime in all sessions whenever location is changed
            newState.sessions = sessions
                ? sessions.map((session) => {
                      let locationTzDateTimeIso = session.locationTzDateTimeIso; // Keep existing value by default
                      let locationTzDate = session.locationTzDate;
                      let locationTzTime = session.locationTzTime;
                      let locationTzDateInfo = session.locationTzDateInfo;

                      if (
                          session.providerTzDateTimeIso &&
                          newLocation &&
                          newLocation.timezone
                      ) {
                          const locTzDate = timezoneConversion(
                              session.providerTzDateTimeIso,
                              newLocation?.id === CLIENT_LOCATION_ID
                                  ? provider
                                      ? provider?.timezone
                                      : companyTz
                                  : newLocation?.timezone
                          );
                          const formattedDateInfo = formatDateInfoAttributes(
                              locTzDate,
                              null,
                              newLocation?.id === CLIENT_LOCATION_ID
                                  ? provider
                                      ? provider?.timezone
                                      : companyTz
                                  : newLocation?.timezone
                          );
                          locationTzDateTimeIso =
                              formattedDateInfo?.dtstamp_str;
                          locationTzDate = formattedDateInfo?.dt_disp;
                          locationTzTime = formattedDateInfo?.label;
                          locationTzDateInfo = formattedDateInfo;
                      }

                      return {
                          ...session,
                          locationTzDateTimeIso,
                          locationTzTime,
                          locationTzDate,
                          locationTzDateInfo
                      };
                  })
                : [];
            newState.locationTz =
                newLocation?.id === CLIENT_LOCATION_ID
                    ? provider
                        ? provider?.timezone
                        : companyTz
                    : newLocation
                      ? newLocation?.timezone
                      : companyTz;

            if (detailValue !== CLIENT_LOCATION_ID) {
                newState.clientAddress = null;
                newState.placeId = null;
            } else {
                //update the booking location to clients address if "My location" is selected
                if (client?.user) {
                    const user = client.user;
                    const hasRequiredFields = Boolean(
                        user?.addressoneline &&
                            user?.country &&
                            user?.latitude &&
                            user?.longitude &&
                            user?.state &&
                            user?.city &&
                            user?.postalcode
                    );

                    if (hasRequiredFields) {
                        newState.clientAddress = {
                            addressoneline: user.addressoneline,
                            country: user.country,
                            countrycode3166alpha2: getCountryCode(
                                user.country
                            ).toLowerCase(),
                            id: CLIENT_LOCATION_ID,
                            latitude: user.latitude,
                            longitude: user.longitude,
                            locationname: user.addressoneline,
                            state: user.state,
                            city: user.city,
                            postalcode: user.postalcode,
                            isRemoteLocation: true,
                            isVirtual: false
                        };
                    }
                }
            }
            break;
        case "selectedPackage":
            const remainingQuantity =
                detailValue?.status && detailValue?.status === "paid"
                    ? detailValue?.initialQuantity - detailValue?.usedQuantity
                    : detailValue?.quantity;
            newState.selectedPackageRemainingQuantity = remainingQuantity;
            if (remainingQuantity && detailValue?.status === "paid") {
                newState.payType = PAY_TYPE_PACKAGE;
                newState.isPkgCredit = true;
            } else {
                newState.payType = PAY_TYPE_CC;
                newState.isPkgCredit = false;
            }
            //we potentially need to update the number of sessions if the new selected package credits are less than the number of sessions already booked
            if (remainingQuantity < sessions?.length) {
                newState.sessions = sessions.slice(0, remainingQuantity);
            }

            //managing repeating sessions when package is selected
            if (isRepeating && detailValue) {
                set(frequencyAtom, "WEEKLY");
                set(endModeAtom, "count");
                set(countAtom, remainingQuantity ?? 1);
                set(intervalAtom, 1);
                set(setWeekdaysAtom, [getDay(sessions[0]?.dateTime)]);
                const upcomingSessions = get(getUpcomingSessionsAtom);
                newState.sessions = upcomingSessions;
            } else if (isRepeating || !detailValue) {
                newState.sessions = sessions.slice(0, 1);
                newState.isRepeating = false;
                set(countAtom, 1);
                set(endModeAtom, "indefinitely");
                set(setWeekdaysAtom, [getDay(sessions[0]?.dateTime)]);
                set(intervalAtom, 1);
                set(frequencyAtom, "WEEKLY");
            } else if (detailValue) {
                set(setWeekdaysAtom, [getDay(sessions[0]?.dateTime)]);
                set(endModeAtom, "count");
                set(frequencyAtom, "WEEKLY");
                set(countAtom, remainingQuantity);

                const upcomingSessions = get(getUpcomingSessionsAtom);
                newState.sessions = upcomingSessions;
                newState.isRepeating = true;
            }
            break;
        case "isRepeating":
            console.log(
                days[getDay(sessions[0]?.dateTime)],
                getDay(sessions[0]?.dateTime)
            );
            /* set(setRecurringDetailAtom, {
                recurringWeekDays: [getDay(sessions[0]?.dateTime)]
            }); */
            set(setWeekdaysAtom, [getDay(sessions[0]?.dateTime)]);
            if (selectedPackage && detailValue === true) {
                set(endModeAtom, "count");
                set(frequencyAtom, "WEEKLY");
                set(countAtom, selectedPackageRemainingQuantity);

                const upcomingSessions = get(getUpcomingSessionsAtom);
                newState.sessions = upcomingSessions;
            } else if (detailValue === true) {
                set(frequencyAtom, "WEEKLY");
                set(endModeAtom, "indefinitely");
                set(intervalAtom, 1);
                set(countAtom, 1);
                const upcomingSessions = get(getUpcomingSessionsAtom);
                newState.sessions = upcomingSessions;
            } else {
                newState.sessions = sessions.slice(0, 1);
                set(endModeAtom, "indefinitely");
                set(countAtom, 1);
                set(setWeekdaysAtom, [0, 1, 2, 3, 4, 5, 6]);
            }
            break;
        case "schedules":
            newState.locations = uniq(
                flatten(
                    detailValue.map(({ locations }) => jsonParse(locations, []))
                )
            );
            break;
        case "sessionProviderId":
            let { providerId } = detailValue;
            if (sessions[detailValue.index]) {
                sessions[detailValue.index].providerId = providerId;
                newState.session = [...sessions];
            }
            newState.providerServices = get(
                getProviderServicesByProviderIdAtom
            )(providerId);
            /*  newState.selectedService = undefined;
            newState.selectedLocationId = undefined; */
            newState.providerId = providerId;
            newState.provider = get(getProviderByProviderIdAtom)(providerId);
            break;
        case "sessionDateTime":
            let {
                dateTime,
                index = 0,
                isSessionDateTimeOptimized: isOptimized
            } = detailValue;
            const selectedService = get(getSelectedServiceAtom);
            const selectedLocation = get(getSelectedLocationAtom);
            //Retrieve the selected dateTime formatted to providers timezone
            const {
                dtstamp_str: providerTzDateTimeIso,
                tz_abbr_disp: providerTzAbbr
            } = formatDateInfoAttributes(
                dateTime,
                selectedService?.minutes || null,
                provider ? provider?.timezone : companyTz
            );
            //Retrive the selected dateTime formatted to locations timezone
            const locTzDate = timezoneConversion(
                providerTzDateTimeIso,
                selectedLocation?.id === CLIENT_LOCATION_ID
                    ? provider
                        ? provider?.timezone
                        : companyTz
                    : selectedLocation
                      ? selectedLocation?.timezone
                      : companyTz
            );

            const locationTzDateInfo = formatDateInfoAttributes(
                locTzDate,
                selectedService?.minutes || null,
                selectedLocation?.id === CLIENT_LOCATION_ID
                    ? provider
                        ? provider?.timezone
                        : companyTz
                    : selectedLocation
                      ? selectedLocation?.timezone
                      : companyTz
            );

            const currentOptimization =
                sessions[index]?.isSessionDateTimeOptimized ?? isOptimized;

            if (sessions[index]) {
                sessions[index].dateTime = dateTime;
                sessions[index].providerTzDateTimeIso = providerTzDateTimeIso;
                sessions[index].providerTzAbbr = providerTzAbbr;
                sessions[index].locationTzDateTimeIso =
                    locationTzDateInfo.dtstamp_str;
                sessions[index].locationTzDate = locationTzDateInfo.dt_disp;
                sessions[index].locationTzTime = locationTzDateInfo.label;
                sessions[index].locationTzDateInfo = locationTzDateInfo;
                sessions[index].isSessionDateTimeOptimized =
                    isOptimized !== null ? isOptimized : currentOptimization;
                newState.sessions = [...sessions];
            }

            if (index === 0 && isRepeating && dateTime) {
                set(startDateAtom, dateTime);
                set(setWeekdaysAtom, [getDay(dateTime)]);
                if (get(endModeAtom) === "endDate") {
                    const threeMonthsLater = new Date(dateTime);
                    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                    set(endDateAtom, threeMonthsLater);
                }
                const upcomingSessions = get(getUpcomingSessionsAtom);
                newState.sessions = upcomingSessions;
            }
            break;
        case "recurringSessionDateTime":
            let { slots, isSessionDateTimeOptimized: risOptimized } =
                detailValue;
            const rselectedService = get(getSelectedServiceAtom);
            const rselectedLocation = get(getSelectedLocationAtom);
            const whoIsAvailableTimezone = get(whoIsAvailableTimezoneAtom);
            slots.forEach(({ timeSlotDateAndTime, ...rest }, index) => {
                const currentOptimization =
                    sessions[index].isSessionDateTimeOptimized;
                //Retrieve the selected dateTime formatted to providers timezone
                const dateTime = utcToZonedTime(
                    new Date(timeSlotDateAndTime),
                    whoIsAvailableTimezone
                );
                const {
                    dtstamp_str: rproviderTzDateTimeIso,
                    tz_abbr_disp: rproviderTzAbbr
                } = formatDateInfoAttributes(
                    dateTime,
                    rselectedService?.minutes || null,
                    provider ? provider?.timezone : companyTz
                );
                //Retrive the selected dateTime formatted to locations timezone
                const rlocTzDate = timezoneConversion(
                    rproviderTzDateTimeIso,
                    rselectedLocation?.id === CLIENT_LOCATION_ID
                        ? provider
                            ? provider?.timezone
                            : companyTz
                        : rselectedLocation
                          ? rselectedLocation?.timezone
                          : companyTz
                );

                const rlocationTzDateInfo = formatDateInfoAttributes(
                    rlocTzDate,
                    rselectedService?.minutes || null,
                    rselectedLocation?.id === CLIENT_LOCATION_ID
                        ? provider
                            ? provider?.timezone
                            : companyTz
                        : rselectedLocation
                          ? rselectedLocation?.timezone
                          : companyTz
                );

                if (sessions[index]) {
                    sessions[index].dateTime = dateTime;
                    sessions[index].providerTzDateTimeIso =
                        rproviderTzDateTimeIso;
                    sessions[index].providerTzAbbr = rproviderTzAbbr;
                    sessions[index].locationTzDateTimeIso =
                        rlocationTzDateInfo.dtstamp_str;
                    sessions[index].locationTzDate =
                        rlocationTzDateInfo.dt_disp;
                    sessions[index].locationTzTime = rlocationTzDateInfo.label;
                    sessions[index].locationTzDateInfo = rlocationTzDateInfo;
                    sessions[index].isSessionDateTimeOptimized =
                        risOptimized !== null
                            ? risOptimized
                            : currentOptimization;
                    newState.sessions = [...sessions];
                    sessions[index].slotType = getSlotType(rest);
                }
            });

            const firstDateTime = new Date(slots[0].timeSlotDateAndTime);
            set(startDateAtom, firstDateTime);
            if (get(endModeAtom) === "endDate") {
                const threeMonthsLater = new Date(firstDateTime);
                threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                set(endDateAtom, threeMonthsLater);
            }
            break;
        case "sessionIsOptimized":
            let { index: sessionIndex, isSessionDateTimeOptimized } =
                detailValue;
            if (sessions[sessionIndex]) {
                sessions[sessionIndex].isSessionDateTimeOptimized =
                    isSessionDateTimeOptimized;
                newState.sessions = [...sessions];
            }
            break;
        case "isBookSeparately":
            if (detailValue === true) {
                newState.providerId = null;
                newState.provider = null;
                newState.providerServices = null;
                newState.providerLocationIds = null;
                newState.sessions = null;
            } else {
                const providerId = get(selectedCalendarProviderIdAtom);
                const dateTime = get(selectedCalendarDateTimeAtom);
                newState.providerId = providerId;
                newState.provider = get(getProviderByProviderIdAtom)(
                    providerId
                );
                newState.providerServices = get(
                    getProviderServicesByProviderIdAtom
                )(providerId);
                newState.providerLocationIds = uniq(
                    flatten(
                        filter(schedules, ({ services }) =>
                            jsonParse(services, []).includes(
                                currentState.selectedServiceId
                            )
                        ).map(({ locations }) => jsonParse(locations, []))
                    )
                );
                newState.sessions = [
                    {
                        providerId: providerId,
                        dateTime: dateTime
                    }
                ];
            }
            break;
        case "cardExpired":
            if (detailValue === true) {
                newState.payType = PAY_TYPE_NEW_CC;
            } else {
                newState.payType = PAY_TYPE_CC;
            }
            break;
        case "client":
            //update the booking location to clients address if "My location" is selected
            if (
                selectedLocationId === CLIENT_LOCATION_ID &&
                detailValue?.user
            ) {
                const user = detailValue.user;
                const hasRequiredFields =
                    user?.addressoneline &&
                    user?.country &&
                    user?.latitude &&
                    user?.longitude &&
                    user?.state &&
                    user?.city &&
                    user?.postalcode;

                if (hasRequiredFields) {
                    newState.clientAddress = {
                        addressoneline: user.addressoneline,
                        country: user.country,
                        countrycode3166alpha2: getCountryCode(
                            user.country
                        ).toLowerCase(),
                        id: CLIENT_LOCATION_ID,
                        latitude: user.latitude,
                        longitude: user.longitude,
                        locationname: user.addressoneline,
                        state: user.state,
                        city: user.city,
                        postalcode: user.postalcode,
                        isRemoteLocation: true,
                        isVirtual: false
                    };
                }
            }
            break;
        default:
            break;
    }
    set(bookingDetailsAtom, { ...currentState, ...newState });
});

export const getUpcomingBookingsDateTimeListAtom = atom((get) => {
    const { sessions, selectedServiceId, isRepeating, provider } =
        get(bookingDetailsAtom);
    const rrule = get(mbRecurringBookingRRuleAtom);
    if (isRepeating && sessions?.length && selectedServiceId) {
        const selectedProvider = provider;
        const selectedService = get(getSelectedServiceAtom);
        if (selectedService) {
            const convertedDateInfo = formatDateInfoAttributes(
                sessions[0].dateTime,
                selectedService.minutes || 60,
                selectedProvider?.timezone
            );
            let bookingsDateTimeList = prepareBookingsDateTimeList(
                convertedDateInfo?.dtstamp_str,
                rrule,
                selectedProvider?.timezone
            );
            return bookingsDateTimeList;
        } else {
            return [];
        }
    } else {
        return [];
    }
});

export const getUpcomingSessionsAtom = atom((get) => {
    const rruleSettings = get(upcomingSessionsRRuleSettingsAtom);
    const selectedService = get(getSelectedServiceAtom);
    const selectedLocation = get(getSelectedLocationAtom);
    const companyTz = get(companyMasterTz);
    const { provider, sessions } = get(bookingDetailsAtom);
    const endMode = get(endModeAtom);
    let modifiedSettings =
        endMode === "indefinitely"
            ? { ...rruleSettings, count: 10 }
            : rruleSettings;
    const rule = new RRule(modifiedSettings);

    const allOccurrences = rule.all();

    return allOccurrences.map((dateTime) => {
        // Get the original session's time components
        const originalTime = sessions[0]?.dateTime
            ? sessions[0]?.dateTime
            : get(startDateAtom);
        const originalHours = originalTime.getHours();
        const originalMinutes = originalTime.getMinutes();

        // Create a new date with the recurring date but original time
        const adjustedDateTime = new Date(dateTime);
        adjustedDateTime.setHours(originalHours, originalMinutes);

        //Retrieve the selected dateTime formatted to providers timezone
        const {
            dtstamp_str: providerTzDateTimeIso,
            tz_abbr_disp: providerTzAbbr
        } = formatDateInfoAttributes(
            adjustedDateTime,
            selectedService?.minutes || null,
            provider ? provider?.timezone : companyTz
        );
        //Retrive the selected dateTime formatted to locations timezone
        const locTzDate = timezoneConversion(
            providerTzDateTimeIso,
            selectedLocation?.id === CLIENT_LOCATION_ID
                ? provider
                    ? provider?.timezone
                    : companyTz
                : selectedLocation
                  ? selectedLocation?.timezone
                  : companyTz
        );

        const locationTzDateInfo = formatDateInfoAttributes(
            locTzDate,
            selectedService?.minutes || null,
            selectedLocation?.id === CLIENT_LOCATION_ID
                ? provider
                    ? provider?.timezone
                    : companyTz
                : selectedLocation
                  ? selectedLocation?.timezone
                  : companyTz
        );

        return {
            dateTime: adjustedDateTime,
            providerTzDateTimeIso,
            providerTzAbbr,
            locationTzDateTimeIso: locationTzDateInfo.dtstamp_str,
            locationTzDate: locationTzDateInfo.dt_disp,
            locationTzTime: locationTzDateInfo.label,
            locationTzDateInfo,
            providerId: provider?.id
        };
    });
});

export const upcomingSessionsRRuleSettingsAtom = atom((get) => {
    const { sessions } = get(bookingDetailsAtom);
    const frequency = get(frequencyAtom);
    const monthlyMode = get(monthlyModeAtom);
    const startDate = sessions[0]?.dateTime
        ? sessions[0]?.dateTime
        : get(startDateAtom); //sessions[0]?.dateTime;
    const weekdays = get(weekdaysAtom);

    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const startDay = startDate.getDate();
    const startHours = startDate.getHours();
    const startMinutes = startDate.getMinutes();
    const startSeconds = startDate.getSeconds();

    const baseSettings = {
        dtstart: new Date(
            Date.UTC(
                startYear,
                startMonth,
                startDay,
                startHours,
                startMinutes,
                startSeconds
            )
        ),
        freq: RRule[frequency.toUpperCase()],
        interval: get(intervalAtom)
    };

    switch (frequency) {
        case "WEEKLY":
            return {
                ...baseSettings,
                byweekday: weekdays.map(
                    (day) =>
                        RRule[["SU", "MO", "TU", "WE", "TH", "FR", "SA"][day]]
                ),
                count:
                    get(endModeAtom) === "count" ? get(countAtom) : undefined,
                until:
                    get(endModeAtom) === "endDate"
                        ? get(endDateAtom)
                        : undefined
            };
        case "MONTHLY":
            if (monthlyMode === "BYDATE") {
                return {
                    ...baseSettings,
                    bymonthday: startDate.getDate(),
                    count:
                        get(endModeAtom) === "count"
                            ? get(countAtom)
                            : undefined,
                    until:
                        get(endModeAtom) === "endDate"
                            ? get(endDateAtom)
                            : undefined
                };
            } else {
                // BYDAY mode - calculate which week of the month
                const dayOfMonth = startDate.getDate();
                const weekOfMonth = Math.ceil(dayOfMonth / 7);
                const dayOfWeek = startDate.getDay();
                return {
                    ...baseSettings,
                    byweekday:
                        RRule[
                            ["SU", "MO", "TU", "WE", "TH", "FR", "SA"][
                                dayOfWeek
                            ]
                        ],
                    bysetpos: weekOfMonth,
                    count:
                        get(endModeAtom) === "count"
                            ? get(countAtom)
                            : undefined,
                    until:
                        get(endModeAtom) === "endDate"
                            ? get(endDateAtom)
                            : undefined
                };
            }
        case "YEARLY":
            return {
                ...baseSettings,
                bymonth: startDate.getMonth() + 1,
                bymonthday: startDate.getDate(),
                count:
                    get(endModeAtom) === "count" ? get(countAtom) : undefined,
                until:
                    get(endModeAtom) === "endDate"
                        ? get(endDateAtom)
                        : undefined
            };
        default:
            return baseSettings;
    }
});

export const setUpcomingSessionsDetailAtom = atom(
    null,
    (get, set, sessions) => {
        const currentState = get(bookingDetailsAtom);
        /* const { selectedPackage } = currentState; */

        const upcomingSessions = get(getUpcomingSessionsAtom);

        set(bookingDetailsAtom, {
            ...currentState,
            sessions: upcomingSessions
        });
    }
);

export const recurIntervalAtom = focusAtom(bookingDetailsAtom, (optic) =>
    optic.prop("recurringPattern")
);

//simply stores the provider id selected in the calendar
export const selectedCalendarProviderIdAtom = atom(null);
export const selectedCalendarDateTimeAtom = atom(null);

// "external" setters not aware of sessions array
export const setBookingProviderIdAtom = atom(null, (get, set, providerId) => {
    const { sessions } = get(bookingDetailsAtom);
    sessions && sessions.length //upsert
        ? set(setDetailAtom, { sessionProviderId: { index: 0, providerId } })
        : set(setDetailAtom, { sessions: [{ providerId }] });
    set(setDetailAtom, {
        providerId: providerId
    });
    set(selectedCalendarProviderIdAtom, providerId);
});
export const setBookingDateTimeAtom = atom(null, (get, set, dateTime) => {
    const { sessions, provider } = get(bookingDetailsAtom);
    const selectedLocation = get(getSelectedLocationAtom);
    const companyTz = get(companyMasterTz);

    //The following is to handle the case where the provider and location are not selected yet
    let tzDateTimeObj = {};
    if (!provider && !selectedLocation) {
        const {
            dtstamp_str: providerTzDateTimeIso,
            tz_abbr_disp: providerTzAbbr
        } = formatDateInfoAttributes(dateTime, null, companyTz);

        const locTzDate = timezoneConversion(dateTime, companyTz);

        const locationTzDateInfo = formatDateInfoAttributes(
            locTzDate,
            null,
            companyTz
        );

        tzDateTimeObj = {
            providerTzDateTimeIso,
            providerTzAbbr,
            locationTzDateTimeIso: locationTzDateInfo.dtstamp_str,
            locationTzDate: locationTzDateInfo.dt_disp,
            locationTzTime: locationTzDateInfo.label,
            locationTzDateInfo
        };
    }

    sessions && sessions.length //upsert
        ? set(setDetailAtom, {
              sessionDateTime: {
                  index: 0,
                  dateTime
              }
          })
        : set(setDetailAtom, {
              sessions: [
                  {
                      dateTime,
                      ...(!provider &&
                          !selectedLocation && {
                              ...tzDateTimeObj
                          })
                  }
              ]
          });
    set(selectedCalendarDateTimeAtom, dateTime);
    set(startDateAtom, dateTime);
});

const fnAtom = atom({ cancel: () => {}, save: () => {} });

export const customTimeActionsAtom = atom(
    (get) => get(fnAtom),
    (get, set, { saveFn, cancelFn }) => {
        set(fnAtom, { save: saveFn, cancel: cancelFn });
    }
);

export const providersListAtom = atom([]);

export const showWhoIsAvailableDialogAtom = atom(false);
export const showOnlyWhoIsAvailableDialogAtom = atom(false); //used to show only the who is available dialog , and hide manual booking form from the UI

export const whoIsAvailableSlotsAtom = atom([]);
export const whoIsAvailableTimezoneAtom = atom(null);

export const whoIsAvailableSlotsNearestDateSortedAtom = atom((get) => {
    const slots = get(whoIsAvailableSlotsAtom);
    const { isRepeating } = get(bookingDetailsAtom);

    return [...slots].sort((a, b) => {
        const aDate = isRepeating
            ? a.slots[0].timeSlotDateAndTime
            : a.timeSlotDateAndTime;
        const bDate = isRepeating
            ? b.slots[0].timeSlotDateAndTime
            : b.timeSlotDateAndTime;
        const dateA = new Date(aDate).getTime();
        const dateB = new Date(bDate).getTime();
        return dateA - dateB;
    });
});
